import {CopilotIcon} from '@primer/octicons-react'
import {Box, Button, IconButton, Link, Text, ButtonGroup} from '@primer/react'
import {Tooltip} from '@primer/react/next'

import {SafeHTMLText} from '@github-ui/safe-html'
import {shortSha} from '../../utils/short-sha'
import {useCurrentRepository} from '@github-ui/current-repository'
import {commitPath, repositoryTreePath} from '@github-ui/paths'
import {CommitAttribution} from '../CommitAttribution'
import {SignedCommitBadge} from '@github-ui/signed-commit-badge'
import type {CommitWithDeferredData} from '../../types/commit-types'
import {useCommitsAppPayload} from '../../hooks/use-commits-app-payload'
import {AsyncChecksStatusBadge} from '../AsyncChecksStatusBadge'
import {Fragment} from 'react'
import {Panel} from '../Panel'
import {useLoadBranchCommits} from '../../hooks/use-load-branch-commits'
import {CommitBranchInfo} from './CommitBranchInfo'
import {CommitTagInfo} from './CommitTagInfo'
import {WeirichCommitBanner} from './WeirichCommitBanner'
import {SpoofedCommitWarningBaner} from './SpoofedCommitWarningBaner'

interface CommitInfoProps {
  commit: CommitWithDeferredData
}

export function CommitInfo({commit}: CommitInfoProps) {
  const repo = useCurrentRepository()
  const {helpUrl} = useCommitsAppPayload()

  let checkStatusCount = ''
  try {
    checkStatusCount = commit.statusCheckStatus?.short_text?.split('checks')[0]?.trim() || ''
  } catch {
    //noop
  }

  const data = useLoadBranchCommits(commit.oid)

  return (
    <Box sx={{px: 3}}>
      {!data.loading && data.branches.length === 0 ? <SpoofedCommitWarningBaner /> : null}
      <WeirichCommitBanner oid={commit.oid} repo={repo} />

      <Panel>
        {/* Title and description */}
        <Box
          sx={{
            display: 'flex',
            backgroundColor: 'canvas.subtle',
            p: 3,
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            flexDirection: 'column',
            borderBottomStyle: 'solid',
            borderColor: 'border.default',
            borderWidth: '1px',
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <SafeHTMLText html={commit.shortMessageMarkdown} sx={{fontSize: 2, fontWeight: 'semibold', mr: 2}} />
              <AsyncChecksStatusBadge
                oid={commit.oid}
                status={commit.statusCheckStatus?.state}
                descriptionString={checkStatusCount}
                repo={repo}
              />
            </Box>
            <Box sx={{display: 'flex'}}>
              <Tooltip text="Browse the repository at this point in the history">
                <Button as={Link} href={repositoryTreePath({repo, action: 'tree', commitish: commit.oid})}>
                  Browse files
                </Button>
              </Tooltip>
              <ButtonGroup sx={{pl: 2}}>
                <Button
                  as={Link}
                  href={`${commitPath({owner: repo.ownerLogin, repo: repo.name, commitish: commit.oid})}.patch`}
                >
                  Raw
                </Button>
                <IconButton icon={CopilotIcon} aria-label="Open GitHub Copilot chat" />
              </ButtonGroup>
            </Box>
          </Box>
          {commit.bodyMessageHtml && (
            <SafeHTMLText
              html={commit.bodyMessageHtml}
              className="ws-pre-wrap"
              sx={{pb: 2, wordBreak: 'break-word', fontFamily: 'var(--fontStack-monospace)', fontSize: '12px'}}
            />
          )}
        </Box>
        <CommitBranchInfo data={data} />
        <CommitTagInfo data={data} />
        {/* Commit attribution */}
        <Box sx={{display: 'flex', justifyContent: 'space-between', p: 3}}>
          <CommitAttribution
            commit={commit}
            repo={repo}
            settings={{fontColor: 'fg.default', avatarSize: 20, fontWeight: 'bold'}}
          >
            <Box sx={{pl: 1}}>
              <SignedCommitBadge
                commitOid={commit.oid}
                hasSignature={true}
                verificationStatus={commit.verifiedStatus}
                signature={{helpUrl, ...commit.signatureInformation!}}
              />
            </Box>
          </CommitAttribution>
          <Box as="pre" sx={{color: 'fg.muted'}}>
            {`${commit.parents.length} parent${commit.parents.length > 1 ? 's' : ''} `}
            {commit.parents.map((parent, index) => {
              return (
                <Fragment key={parent}>
                  {index !== 0 ? ' + ' : ''}
                  <Link
                    href={commitPath({owner: repo.ownerLogin, repo: repo.name, commitish: parent})}
                    sx={{color: 'fg.default'}}
                  >
                    {shortSha(parent)}
                  </Link>
                </Fragment>
              )
            })}
            {' commit '}
            <Text sx={{color: 'fg.default'}}>{shortSha(commit.oid)}</Text>
          </Box>
        </Box>
      </Panel>
    </Box>
  )
}

try{ CommitInfo.displayName ||= 'CommitInfo' } catch {}